import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { appName } from "../../../../app.json";
import ListPatientAssessment from "./ListPatientAssessment";
import { LIST, CREATE, SINGLE } from "../../../../queries/patient-assessment.query";
import { useQuery, useMutation, useLazyQuery } from "@apollo/react-hooks";
import Pagination from "../../../../partials/pagination";
import LoadingSpinner from "../../../../partials/loading";
import NewAssessmentForm from "./NewAssessmentForm";
import { ReadData } from "../../../../providers/Patient.Provider";
import AssessmentDetail from "./DetailAssessment";
import { toast } from "react-toastify";
import { CleanMessage } from "../../../../util/app";

function Assessment({ match, location }) {
    // local state
    const [page, setPage] = useState(1);
    const [limit] = useState(25);
    const [newItem, setNewItem] = useState(false);
    const [activeItem, setActiveItem] = useState(null);

    // get assessment id
    const assessment = match.params.id;
    const patient = ReadData();

    const title = "Patient Assessment";

    /**
     * A function that scrolls the page to top
     */
    function scrollTop() {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }
    // fetch data
    const { loading, data, fetchMore } = useQuery(LIST, {
        variables: {
            page,
            limit,
            assessment,
            id: patient.id,
        },
        onError: (e) => toast.error(CleanMessage(e.message)),
    });
    // fetch data
    const [getSingle, { loading: sLoading }] = useLazyQuery(SINGLE, {
        onCompleted: (data) => {
            setActiveItem(data.GetPatientAssessment.doc);
        },
        onError: (e) => toast.error(CleanMessage(e.message)),
    });

    // Mutation
    const [createFunc, { loading: cLoading }] = useMutation(CREATE, {
        update: (caches, cb) => {
            const { GetPatientAssessments } = caches.readQuery({
                query: LIST,
                variables: {
                    page,
                    limit,
                    assessment,
                    id: patient.id,
                },
            });
            GetPatientAssessments.docs.unshift(cb.data.RecordPatientAssessment.doc);
            GetPatientAssessments.totalDocs++;
            caches.writeQuery({
                query: LIST,
                variables: {
                    page,
                    limit,
                    assessment,
                    id: patient.id,
                },
                data: { GetPatientAssessments },
            });
        },
        onError: (e) => toast.error(CleanMessage(e.message)),
        onCompleted: (d) => {
            toast.success(d.RecordPatientAssessment.message);
        },
    });

    return (
        <>
            <Helmet>
                <title>
                    {title} | {appName}
                </title>
            </Helmet>

            {!newItem && !activeItem && (
                <div className="col-md-12 fade-in">
                    <div className="card m-t-20">
                        <div className="card-head">
                            <header>{data && data.GetAssessment.doc.name}</header>
                            <div className="float-right mt-2 mr-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        scrollTop();
                                        setNewItem(true);
                                        setActiveItem(null);
                                    }}
                                    data-toggle="tab"
                                    className="mdl-button mdl-button--raised mdl-js-ripple-effect m-b-10 btn-circle btn-success"
                                >
                                    <i className="material-icons"> add</i> New
                                </button>
                            </div>
                        </div>
                        <div className="card-body" style={{ minHeight: "420px" }}>
                            <LoadingSpinner loading={loading} />
                            {!loading && (
                                <ListPatientAssessment
                                    items={data && data.GetPatientAssessments.docs}
                                    onView={(item) => {
                                        setNewItem(false);
                                        scrollTop();
                                        setActiveItem(item);
                                        getSingle({
                                            variables: { id: item.id },
                                        });
                                    }}
                                />
                            )}
                        </div>
                        <div className="card-footer">
                            {data && (
                                <Pagination
                                    {...data.GetPatientAssessments}
                                    length={data.GetPatientAssessments.docs.length}
                                    onPageClicked={(page) => {
                                        setPage(page);
                                        fetchMore({
                                            variables: {
                                                page,
                                            },
                                            updateQuery: (prev, { fetchMoreResult }) => {
                                                if (!prev) return prev;
                                                return {
                                                    GetPatientAssessments: fetchMoreResult.GetPatientAssessments,
                                                };
                                            },
                                        });
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            {newItem && !activeItem && (
                <div className="col-md-12 fade-in">
                    <div className="card">
                        <div className="card-head">
                            <header>
                                <button
                                    onClick={() => {
                                        setNewItem(false);
                                        setActiveItem(null);
                                        scrollTop();
                                    }}
                                    title="Back"
                                    className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                >
                                    <i className="material-icons text-primary">keyboard_backspace</i>
                                </button>
                                Record New Assessment
                            </header>
                        </div>
                        <div className="card-body m-b-20">
                            <LoadingSpinner loading={cLoading} />
                            <NewAssessmentForm
                                items={data && data.GetAssessmentQuestionsByCategory.docs}
                                more={data && data.GetAssessment.doc.additional}
                                scores={data && data.GetAssessment.doc.scoreGuide}
                                onCancel={() => {
                                    setNewItem(false);
                                    scrollTop();
                                }}
                                onSubmit={async (item) => {
                                    document.body.scrollTop = 0;
                                    document.documentElement.scrollTop = 0;
                                    const { confirmed, ...rest } = item;
                                    await createFunc({
                                        variables: {
                                            model: {
                                                ...rest,
                                                patient: patient.id,
                                                admission: patient.lastAdmission,
                                            },
                                            assessment,
                                            confirm: confirmed,
                                        },
                                    });
                                    // console.log(JSON.stringify(item, null, 6));
                                    setTimeout(() => {
                                        setNewItem(false);
                                        scrollTop();
                                    }, 500);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {!newItem && activeItem && (
                <div className="col-md-12 fade-in">
                    <div className="card">
                        <div className="card-head">
                            <header>
                                <button
                                    onClick={() => {
                                        setNewItem(false);
                                        setActiveItem(null);
                                        scrollTop();
                                    }}
                                    title="Back"
                                    className="mdl-button mdl-button--icon margin-right-10 mdl-button--colored"
                                >
                                    <i className="material-icons text-primary">keyboard_backspace</i>
                                </button>
                                {data && data.GetAssessment.doc.name} Detail
                            </header>
                        </div>
                        <div className="card-body m-b-20">
                            <LoadingSpinner loading={sLoading} />
                            {!sLoading && <AssessmentDetail item={activeItem} />}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
export default Assessment;
